/**
 * One row of the Entity Management grid, exactly as
 * `GET /api/entity-management/screen` returns it: the columns the table, its
 * KPI strip and the entity search read, with the entity type and profiles
 * already named.
 *
 * The property names are the ones those already read off an `EntityParams`
 * (`entityName`, `mei`, `internalId`, `systemId`, `status`, `taxFormStatus`,
 * `kycStatus`), so they run unchanged. It is not a whole entity: the edit form
 * behind the row menu fetches the one entity it acts on, by id, when it opens.
 */
export interface EntityScreenRow {
  id: string
  status: 'Active' | 'Inactive'
  entityName: string
  internalId: string
  /** Assigned on first activation: '' before. Searchable, not a column. */
  systemId: string
  mei: string
  kycType: string
  entityTypeId: string | null
  /** '' when the type is not visible to the caller. */
  entityTypeName: string
  entityProfileIds: string[]
  /** One per id, in order; '' for a profile not visible to the caller. */
  entityProfileNames: string[]
  parent: string
  fundManager: string
  taxFormStatus: string
  kycStatus: string
}

/** The Type column: the type's name, or `N/A` when there is none to show. */
export const entityTypeLabel = (row: EntityScreenRow): string =>
  row.entityTypeName || 'N/A'

/** The Profile column: nothing for none, the name for one, `Multiple` for more. */
export const entityProfileLabel = (row: EntityScreenRow): string =>
  row.entityProfileIds.length === 0
    ? ''
    : row.entityProfileIds.length > 1
      ? 'Multiple'
      : (row.entityProfileNames[0] ?? '')

/** The Fund Manager / Parent column: the parent, else the fund manager. */
export const entityParentLabel = (row: EntityScreenRow): string =>
  row.parent || row.fundManager || ''
